import { isEmpty, omit, isNull } from 'lodash';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { ISavedFilter } from '../../../../../components/sidebar-filters/saved-filter/interface';
import { PjmLocationFilterKeys, TPjmLocationFilterKey } from './PjmLocationSidebarFilters';
import { ISidebarFilterOptionsContext } from '../../../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';

export const transformPjmLocationsSavedFilterToUser = (
    filter: ISavedFilter['filter_values'],
    context: ISidebarFilterOptionsContext
): Record<TPjmLocationFilterKey, string> => {
    let result: Record<TPjmLocationFilterKey, string> = {} as Record<TPjmLocationFilterKey, string>;

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid displaying them as "unknown"
        if (isUndefined(value) || isNull(value)) continue;

        switch (key) {
            case PjmLocationFilterKeys.status:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmLocationStatusOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case PjmLocationFilterKeys.zone:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmLocationZoneOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case PjmLocationFilterKeys.state:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmLocationStateOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case PjmLocationFilterKeys.zipCode:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmLocationZipCodeOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case PjmLocationFilterKeys.edc:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmLocationEdcOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case PjmLocationFilterKeys.syncDrhubStatus:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmLocationSyncDrhubStatusOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    // Remove empty properties from the result object
    const emptyProperties = getEmptyFilterProperties(result);
    if (emptyProperties.length > 0) {
        result = omit(result, emptyProperties) as Record<TPjmLocationFilterKey, string>;
    }

    return result;
};

export const transformPjmLocationsSavedFilterValuesFromDto = (filter: ISavedFilter['filter_values']): Record<TPjmLocationFilterKey, unknown> => {
    const result: Record<TPjmLocationFilterKey, unknown> = {} as Record<TPjmLocationFilterKey, unknown>;

    // Convert values from DTO format to interface format
    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value)) continue;

        switch (key) {
            case PjmLocationFilterKeys.status:
            case PjmLocationFilterKeys.zone:
            case PjmLocationFilterKeys.state:
            case PjmLocationFilterKeys.zipCode:
            case PjmLocationFilterKeys.edc:
            case PjmLocationFilterKeys.syncDrhubStatus:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = value; // Keep other values as is
        }
    }

    return result;
};

export const transformPjmLocationsSavedFilterValuesToDto = (filter: ISavedFilter['filter_values']): Record<TPjmLocationFilterKey, unknown> => {
    const result: Record<TPjmLocationFilterKey, unknown> = {} as Record<TPjmLocationFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        switch (key) {
            case PjmLocationFilterKeys.status:
            case PjmLocationFilterKeys.zone:
            case PjmLocationFilterKeys.state:
            case PjmLocationFilterKeys.zipCode:
            case PjmLocationFilterKeys.edc:
            case PjmLocationFilterKeys.syncDrhubStatus:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    return result;
};

export const checkFilterInvalidValues = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    const invalidFields: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        if (isNil(value) || isEmpty(value)) continue;

        switch (key) {
            case PjmLocationFilterKeys.status:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmLocationStatusOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case PjmLocationFilterKeys.zone:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmLocationZoneOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case PjmLocationFilterKeys.state:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmLocationStateOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case PjmLocationFilterKeys.zipCode:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmLocationZipCodeOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case PjmLocationFilterKeys.edc:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmLocationEdcOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case PjmLocationFilterKeys.syncDrhubStatus:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmLocationSyncDrhubStatusOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
        }
    }

    return invalidFields;
};

export function countNotEmptyFilters(filters: Record<string, any>): number {
    if (!filters) return 0;

    return Object.values(filters).filter(value => {
        if (Array.isArray(value)) return value.length > 0;

        if (typeof value === 'number') return !isNaN(value);

        if (typeof value === 'string') return value.trim() !== '';

        if (typeof value === 'boolean') return true;

        return false;
    }).length;
}

function getEmptyFilterProperties(obj: Record<TPjmLocationFilterKey, string>): TPjmLocationFilterKey[] {
    return Object.entries(obj)
        .filter(([_, value]) => isEmpty(value))
        .map(([key, _]) => key) as TPjmLocationFilterKey[];
}
